import { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal, X, RotateCcw, ChevronDown } from "lucide-react";
import { Header } from "@/components/Header";
import { OfferCard } from "@/components/OfferCard";
import { PreventivoSection } from "@/components/PreventivoSection";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { getOfferte } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const MARCHE_EU = [
  "Abarth", "Aiways", "Alfa Romeo", "Aston Martin", "Audi", "Bentley", "BMW", "BYD",
  "Citroën", "Cupra", "Dacia", "DFSK", "DR", "DS", "Ferrari", "Fiat", "Ford",
  "GWM Ora", "Honda", "Hyundai", "Ineos", "Jaecoo", "Jaguar", "Jeep", "Kia",
  "Lamborghini", "Lancia", "Land Rover", "Leapmotor", "Lexus", "Lotus", "Lynk & Co",
  "Maserati", "Maxus", "Mazda", "McLaren", "Mercedes-Benz", "MG", "Mini", "Nio",
  "Nissan", "Omoda", "Opel", "Peugeot", "Polestar", "Porsche", "Renault", "Rolls-Royce",
  "Seat", "Skoda", "Smart", "Subaru", "Suzuki", "Tesla", "Toyota", "Volkswagen", "Volvo", "Xpeng",
];

const ALIMENTAZIONI = ["Benzina", "Diesel", "Hybrid", "Plug-in Hybrid", "Elettrico"];

const PREZZI = [
  { label: "Tutti i canoni", min: 0, max: Infinity },
  { label: "Fino a € 300", min: 0, max: 300 },
  { label: "€ 300 – 600", min: 300, max: 600 },
  { label: "€ 600 – 1.000", min: 600, max: 1000 },
  { label: "Oltre € 1.000", min: 1000, max: Infinity },
];

const TARGETS = [
  { value: "", label: "Tutti" },
  { value: "privati", label: "Privati" },
  { value: "partita-iva", label: "Partita IVA" },
  { value: "aziende", label: "Aziende" },
];

const ORDINI = [
  { value: "evidenza", label: "In evidenza" },
  { value: "prezzo-asc", label: "Canone: dal più basso" },
  { value: "prezzo-desc", label: "Canone: dal più alto" },
];

const FilterGroup = ({ title, children }) => (
  <div className="border-b border-xmove-ink/8 py-5 last:border-0">
    <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-xmove-muted">{title}</h3>
    {children}
  </div>
);

export default function Offerte() {
  const [offerte, setOfferte] = useState([]);
  const [q, setQ] = useState("");
  const [marca, setMarca] = useState("");
  const [target, setTarget] = useState("");
  const [prezzo, setPrezzo] = useState(0);
  const [alimentazioni, setAlimentazioni] = useState([]);
  const [ordine, setOrdine] = useState("evidenza");
  const [drawer, setDrawer] = useState(false);

  useSeo({
    title: "Offerte Noleggio Lungo Termine: Tutti i Modelli | XMOVE",
    description:
      "Tutte le offerte di noleggio lungo termine disponibili: filtra per marca, prezzo, alimentazione e profilo. Canone tutto incluso, preventivo gratuito in pochi minuti.",
    path: "/offerte/",
  });

  useEffect(() => {
    getOfferte().then(setOfferte).catch(() => setOfferte([]));
  }, []);

  const alimDisp = ALIMENTAZIONI;
  const marcheDisp = useMemo(() => [...new Set([...MARCHE_EU, ...offerte.map((o) => o.marca)])].sort(), [offerte]);
  const ivaInclusa = target === "privati";

  const filtrate = useMemo(() => {
    const range = PREZZI[prezzo];
    let list = offerte.filter((o) => {
      if (q && !`${o.marca} ${o.modello} ${o.allestimento}`.toLowerCase().includes(q.toLowerCase())) return false;
      if (marca && o.marca !== marca) return false;
      if (target && !o.target.includes(target)) return false;
      if (alimentazioni.length && !alimentazioni.includes(o.alimentazione)) return false;
      if (o.canone == null || o.canone < range.min || o.canone >= range.max) return false;
      return true;
    });
    if (ordine === "evidenza") list = [...list].sort((a, b) => (b.vetrina ? 1 : 0) - (a.vetrina ? 1 : 0) || (a.vetrina_ordine ?? 999) - (b.vetrina_ordine ?? 999) || (a.canone ?? 999999) - (b.canone ?? 999999));
    if (ordine === "prezzo-asc") list = [...list].sort((a, b) => a.canone - b.canone);
    if (ordine === "prezzo-desc") list = [...list].sort((a, b) => b.canone - a.canone);
    return list;
  }, [offerte, q, marca, target, prezzo, alimentazioni, ordine]);

  const toggleAlim = (v) =>
    setAlimentazioni(alimentazioni.includes(v) ? alimentazioni.filter((x) => x !== v) : [...alimentazioni, v]);

  const reset = () => {
    setQ(""); setMarca(""); setTarget(""); setPrezzo(0); setAlimentazioni([]); setOrdine("evidenza");
  };

  const nFiltri = (marca ? 1 : 0) + alimentazioni.length + (target ? 1 : 0) + (prezzo ? 1 : 0) + (q ? 1 : 0);

  const filtersBody = (
    <>
      <FilterGroup title="Cerca">
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xmove-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Es. GLC Coupé, BMW…"
            data-testid="offerte-search-input"
            className="w-full rounded-xl border border-xmove-ink/15 bg-xmove-cream py-2.5 pl-9 pr-3 text-sm outline-none transition-all focus:border-xmove-orange focus:ring-2 focus:ring-xmove-orange/20"
          />
        </div>
      </FilterGroup>
      <FilterGroup title="Per chi">
        <div className="flex flex-wrap gap-2">
          {TARGETS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTarget(t.value)}
              data-testid={`offerte-target-${t.value || "tutti"}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${target === t.value ? "bg-xmove-orange text-white" : "border border-xmove-ink/15 text-xmove-ink hover:border-xmove-orange"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </FilterGroup>
      <FilterGroup title="Marca">
        <div className="relative">
          <select
            value={marca}
            onChange={(e) => setMarca(e.target.value)}
            data-testid="offerte-marca-select"
            className="w-full appearance-none rounded-xl border border-xmove-ink/15 bg-xmove-cream px-3.5 py-2.5 pr-9 text-sm text-xmove-ink outline-none transition-all focus:border-xmove-orange focus:ring-2 focus:ring-xmove-orange/20"
          >
            <option value="">Tutte le marche</option>
            {marcheDisp.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <ChevronDown size={15} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xmove-muted" />
        </div>
      </FilterGroup>
      <FilterGroup title="Canone mensile">
        <div className="space-y-2">
          {PREZZI.map((p, i) => (
            <label key={p.label} className="flex cursor-pointer items-center gap-2.5 text-sm text-xmove-ink">
              <input type="radio" name="prezzo" checked={prezzo === i} onChange={() => setPrezzo(i)} data-testid={`offerte-prezzo-${i}`} className="h-4 w-4 accent-xmove-orange" />
              {p.label}
            </label>
          ))}
        </div>
      </FilterGroup>
      <FilterGroup title="Alimentazione">
        <div className="space-y-2">
          {alimDisp.map((a) => (
            <label key={a} className="flex cursor-pointer items-center gap-2.5 text-sm text-xmove-ink">
              <input type="checkbox" checked={alimentazioni.includes(a)} onChange={() => toggleAlim(a)} data-testid={`offerte-alim-${a.toLowerCase().replace(/\s/g, "-")}`} className="h-4 w-4 accent-xmove-orange" />
              {a}
              <span className="ml-auto text-xs text-xmove-muted">{offerte.filter((o) => o.alimentazione === a && o.canone != null).length}</span>
            </label>
          ))}
        </div>
      </FilterGroup>
      <button onClick={reset} data-testid="offerte-reset-btn" className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-xmove-ink/15 py-2.5 text-xs font-bold text-xmove-muted transition-colors hover:border-xmove-orange hover:text-xmove-orange">
        <RotateCcw size={13} /> Azzera filtri {nFiltri > 0 && `(${nFiltri})`}
      </button>
    </>
  );

  return (
    <div className="pb-[72px] md:pb-0" data-testid="offerte-page">
      <Header />
      <main>
        <section className="border-b border-xmove-ink/8 bg-white">
          <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8 lg:py-14">
            <Reveal>
              <h1 className="text-balance font-display text-3xl font-bold text-xmove-ink sm:text-4xl lg:text-5xl" data-testid="offerte-h1">
                Tutte le nostre offerte di noleggio auto lungo termine.
              </h1>
              <p className="mt-3 max-w-2xl text-base text-xmove-muted md:text-lg">
                Tutti i modelli disponibili, selezionati dallo stock più ampio d'Italia.
                Canone tutto incluso, preventivo gratuito in pochi minuti.
              </p>
            </Reveal>
          </div>
        </section>

        <div className="mx-auto max-w-7xl gap-8 px-5 py-10 lg:grid lg:grid-cols-[280px_1fr] lg:px-8">
          <aside className="hidden lg:block">
            <div
              className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain rounded-2xl border border-xmove-ink/8 bg-white p-6 shadow-soft"
              data-lenis-prevent
              data-testid="offerte-filters"
            >
              {filtersBody}
            </div>
          </aside>

          <div>
            <div className="mb-6 flex items-center justify-between gap-3">
              <p className="text-sm text-xmove-muted" data-testid="offerte-count">
                <b className="text-xmove-ink">{filtrate.length}</b> {filtrate.length === 1 ? "auto disponibile" : "auto disponibili"}
              </p>
              <div className="flex items-center gap-2">
                <select value={ordine} onChange={(e) => setOrdine(e.target.value)} data-testid="offerte-sort-select" className="rounded-xl border border-xmove-ink/15 bg-white px-3.5 py-2.5 text-xs font-semibold text-xmove-ink outline-none focus:border-xmove-orange">
                  {ORDINI.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <button onClick={() => setDrawer(true)} data-testid="offerte-filters-open-btn" className="flex items-center gap-2 rounded-xl bg-xmove-night px-4 py-2.5 text-xs font-bold text-white lg:hidden">
                  <SlidersHorizontal size={14} /> Filtri {nFiltri > 0 && `(${nFiltri})`}
                </button>
              </div>
            </div>

            {filtrate.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-xmove-ink/20 bg-white p-12 text-center" data-testid="offerte-empty">
                <p className="font-display text-lg font-bold text-xmove-ink">Nessuna auto con questi filtri</p>
                <p className="mt-2 text-sm text-xmove-muted">Azzera i filtri o chiedici un preventivo: l'auto che cerchi potrebbe arrivare a breve nello stock.</p>
                <button onClick={reset} data-testid="offerte-empty-reset-btn" className="mt-5 rounded-xl bg-xmove-orange px-6 py-3 text-sm font-bold text-white hover:bg-xmove-orangeDark">
                  Azzera filtri
                </button>
              </div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="offerte-results">
                {filtrate.map((o, i) => (
                  <Reveal key={`${o.slug_marca}-${o.slug_modello}`} delay={(i % 3) * 0.07} className="h-full">
                    <OfferCard offer={o} ivaInclusa={ivaInclusa} />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>

        {drawer && (
          <div className="fixed inset-0 z-[70] lg:hidden" data-testid="offerte-filters-drawer">
            <div className="absolute inset-0 bg-xmove-night/50" onClick={() => setDrawer(false)} />
            <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-white p-6 pb-24">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-xmove-ink">Filtra le offerte</h2>
                <button onClick={() => setDrawer(false)} data-testid="offerte-filters-close-btn" aria-label="Chiudi filtri" className="text-xmove-muted"><X size={22} /></button>
              </div>
              {filtersBody}
              <button onClick={() => setDrawer(false)} data-testid="offerte-filters-apply-btn" className="mt-4 w-full rounded-xl bg-xmove-orange py-3.5 text-sm font-bold text-white">
                Mostra {filtrate.length} risultati
              </button>
            </div>
          </div>
        )}

        <PreventivoSection
          idPrefix="offerte"
          title="Non trovi l'auto giusta? Te la troviamo noi"
          subtitle="Indicaci modello o budget: consultiamo lo stock più ampio d'Italia e ti richiamiamo in pochi minuti con la proposta migliore."
        />
      </main>
      <Footer />
      <MobileStickyBar waText={undefined} />
    </div>
  );
}
